import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { fmtDate, label } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const ACTION = { approved: "Approved", partially_vetoed: "Approved with partial veto", deemed_approved: "Deemed approved (no action within the period)", vetoed: "Vetoed" };
const longDate = (v) => (v ? new Date(/T/.test(v) ? (/Z$/.test(v) ? v : `${v}Z`) : `${v}T00:00:00+08:00`).toLocaleDateString("en-PH", { timeZone: "Asia/Manila", year: "numeric", month: "long", day: "numeric" }) : "—");

function IssueForm({ id, onIssued }) {
  const [f, setF] = useState({ requested_by: "", purpose: "", or_number: "" });
  const issue = () => api.post(`/measures/${id}/certified-copies`, { ...f, or_number: f.or_number || null }).then((r) => { toast.success(`Issued ${r.data.control_no}`); onIssued(r.data.id); })
    .catch((e) => toast.error(errMsg(e)));
  return (
    <div className="grid gap-2 md:grid-cols-[1fr_1fr_140px_auto] md:items-end" data-testid="ctc-issue-form">
      {[["requested_by", "Requested by"], ["purpose", "Purpose"], ["or_number", "O.R. no. (optional)"]].map(([k, l]) => (
        <label key={k} className="text-[11px]">{l}<input className="otms-input mt-0.5" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`ctc-${k}`} /></label>
      ))}
      <Button size="sm" onClick={issue} disabled={!f.requested_by || !f.purpose} data-testid="ctc-issue-button">Issue and assign control no.</Button>
    </div>
  );
}

function Details({ d }) {
  const ea = d.executive, vote = d.approval.vote, eff = d.effectivity;
  const rows = [
    ["Approved by the Sanggunian", vote ? `${longDate(vote.session_at || vote.finalized_at)}, ${label(vote.session_type)} Session No. ${vote.session_no} — ${vote.yes_count} in favor, ${vote.no_count} against, ${vote.abstain_count} abstaining, ${vote.absent_count} absent` : longDate(d.approval.date)],
    ea && ["Executive action", `${ACTION[ea.action] || "—"} on ${longDate(ea.action_date)}${ea.override_status === "overridden" ? `; veto overridden by the Sanggunian on ${longDate(ea.override_date)}` : ""}`],
    ea?.vetoed_items && ["Vetoed items", ea.vetoed_items],
    d.review && d.review.status !== "pending" && ["External review", `${d.review.reviewing_body}: ${label(d.review.status)} on ${longDate(d.review.findings_date)}`],
    ...d.publications.map((p) => [p.kind === "posting" ? "Posted at" : p.kind === "newspaper" ? "Published in" : "Published online", `${p.place_or_outlet} — ${longDate(p.start_date)}${p.end_date ? ` to ${longDate(p.end_date)}` : ""}`]),
    ["Date of effectivity", eff?.effectivity_date ? `${longDate(eff.effectivity_date)}${eff.verified_at ? " (verified)" : ""}` : "—"],
  ].filter(Boolean);
  return (
    <table className="mb-6 w-full border-collapse text-[13px]" data-testid="ctc-details"><tbody>
      {rows.map(([k, v], i) => <tr key={i}><td className="w-48 border border-black px-2 py-1 align-top font-semibold">{k}</td><td className="border border-black px-2 py-1">{v}</td></tr>)}
    </tbody></table>
  );
}

export default function CertifiedCopyPrint() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { user, can } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => api.get(`/measures/${id}/certified-copy`, { params: { issue: params.get("issue") || undefined } }).then((r) => setD(r.data)).catch((e) => setError(errMsg(e))), [id, params]);
  useEffect(() => { load(); }, [load]);
  if (error) return <div className="p-8 text-sm text-red-700" data-testid="ctc-error">{error}</div>;
  if (!d) return <div className="p-8 text-sm">Loading…</div>;
  const issue = d.issuances.find((c) => c.id === params.get("issue"));
  const v = d.version, m = d.measure;
  return (
    <div className="relative mx-auto max-w-3xl overflow-hidden bg-white p-10 font-serif text-[15px] leading-relaxed text-black" data-testid="ctc-print-view">
      <div className="no-print mb-6 space-y-3 rounded border border-slate-200 bg-slate-50 p-3 font-sans text-xs">
        {d.problems.length > 0 ? (
          <div className="text-red-700" data-testid="ctc-problems">Cannot be certified yet: {d.problems.join("; ")}.</div>
        ) : issue ? (
          <div className="flex flex-wrap items-center justify-between gap-2"><span data-testid="ctc-issued-info">Control no. <b>{issue.control_no}</b> issued to {issue.requested_by}.</span>
            <Button size="sm" onClick={() => window.print()} data-testid="ctc-print-button">Print / Save as PDF</Button></div>
        ) : can("measure.certify") ? <IssueForm id={id} onIssued={(cid) => setParams({ issue: cid })} />
          : <div>Only the Office of the Secretary can issue a certified true copy. This preview is not valid.</div>}
      </div>
      {!issue && <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-7xl font-bold uppercase text-slate-200 [transform:rotate(-30deg)]" data-testid="ctc-draft-watermark">Preview — not certified</div>}
      <header className="mb-6 text-center">
        <div className="text-xs uppercase tracking-widest">Republic of the Philippines</div>
        <div className="font-semibold uppercase">{user.lgu?.name}</div>
        <div className="text-sm uppercase">{user.lgu?.legislative_body_name}</div>
        <div className="text-xs">Office of the Secretary to the Sanggunian</div>
        <div className="mt-4 inline-block border-2 border-black px-4 py-1 text-sm font-bold uppercase tracking-widest" data-testid="ctc-stamp">Certified True Copy</div>
      </header>
      <section className="relative mb-6">
        <div className="text-center font-bold uppercase" data-testid="ctc-number">{m.official_number}</div>
        <div className="mb-4 text-center font-bold uppercase">{v?.title || m.measure_title}</div>
        {m.sponsor && <div className="mb-3 text-center text-sm italic">Sponsored by {m.sponsor}</div>}
        {v?.whereas_clauses && <div className="mb-3 whitespace-pre-line text-justify">{v.whereas_clauses}</div>}
        {v?.enacting_clause && <div className="mb-3 whitespace-pre-line font-semibold">{v.enacting_clause}</div>}
        <div className="whitespace-pre-line text-justify" data-testid="ctc-body">{v?.body}</div>
      </section>
      <Details d={d} />
      <section className="break-inside-avoid text-sm">
        <p className="mb-8 indent-8 text-justify">I HEREBY CERTIFY that the foregoing is a true and correct copy of {m.official_number}, {v?.title || m.measure_title}, as
          enacted and on file with the Office of the Secretary to the Sanggunian, and that the approval, posting and effectivity details stated above are accurate as recorded.</p>
        <p className="mb-10">Issued this {longDate(issue?.issued_at)} at {user.lgu?.name}{issue ? `, upon the request of ${issue.requested_by} for ${issue.purpose}` : ""}.</p>
        <div className="ml-auto w-72 text-center">
          <div className="border-t border-black pt-1 font-semibold uppercase" data-testid="ctc-secretary">{d.secretary || "\u00a0"}</div>
          <div className="text-xs">Secretary to the Sanggunian</div>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 text-xs">
          <div>Control no.: <b data-testid="ctc-control-no">{issue?.control_no || "—"}</b></div>
          <div>O.R. no.: {issue?.or_number || "—"} &nbsp; Date: {issue ? fmtDate(issue.issued_at) : "—"}</div>
        </div>
      </section>
      <footer className="mt-6 border-t pt-1 font-mono text-[10px] text-gray-500">Text version {v?.version_no} · SHA-256 {v?.content_hash?.slice(0, 16)}… · {m.tracking_ref}</footer>
    </div>
  );
}
